/**
 * "Add blocked period" (Wave Experience A, item 4; UAT finding 17): declare a shutdown, a
 * holiday or an event on the calendar, for the principals holding CALENDAR_PERIOD CREATE.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Offered only when the server's options name somewhere to declare one (tenant-wide or
 * projects, GET /api/calendar-periods/create-options); the POST re-checks every one. The
 * project picker draws identifier and name in two columns.
 */

import { useState } from "react";

import { Input } from "@/components/ui/input";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";

import { createCalendarPeriod, type PeriodCreateOptions } from "../api/calendar";
import { ApiError } from "../api/client";
import type { TaxonomyToken } from "../api/portfolio";
import { useToken } from "../auth/sessionContext";
import { strings } from "../brand/strings";
import { ServerField } from "../lib/serverErrors";
import { ActionDialog } from "../nav/ActionDialog";
import { PICKER_CLASS, PickerOption, pickerWidth } from "../nav/PickerOption";

const TENANT_WIDE = "";

export function BlockedPeriodDialog({
  options,
  kinds,
  onClose,
  onAdded,
}: {
  readonly options: PeriodCreateOptions;
  readonly kinds: readonly TaxonomyToken[];
  readonly onClose: () => void;
  readonly onAdded: (name: string) => void;
}): React.JSX.Element {
  const token = useToken();
  const copy = strings.calendar;
  const [chosenKind, setKind] = useState("");
  // The kinds may arrive after the dialog opens; the first one is the default until chosen.
  const kind = chosenKind !== "" ? chosenKind : (kinds[0]?.token ?? "");
  const [name, setName] = useState("");
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [projectId, setProjectId] = useState(
    options.tenantWide ? TENANT_WIDE : (options.projects[0]?.id ?? TENANT_WIDE),
  );
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const width = pickerWidth(options.projects.map((one) => one.humanId));
  const ready = kind !== "" && name.trim() !== "" && startsOn !== "" && endsOn !== "";

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await createCalendarPeriod(token, {
        kind,
        name: name.trim(),
        startsOn,
        endsOn,
        projectId: projectId === TENANT_WIDE ? null : projectId,
        note: note.trim() === "" ? null : note.trim(),
      });
      onAdded(name.trim());
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err : new ApiError("unavailable", 500, null));
    } finally {
      setBusy(false);
    }
  }

  return (
    <ActionDialog open title={copy.blockedHeading} error={error} onClose={onClose}>
      <p className="q-note">{copy.blockedLede}</p>
      <div className="q-field">
        <label className="q-field__label" htmlFor="blocked-project">
          {copy.blockedProject}
        </label>
        <ServerField field="project_id">
          <NativeSelect
            id="blocked-project"
            data-testid="blocked-project"
            className={PICKER_CLASS}
            value={projectId}
            onChange={(event) => {
              setProjectId(event.target.value);
            }}
          >
            {options.tenantWide && <option value={TENANT_WIDE}>{copy.blockedTenantWide}</option>}
            {options.projects.map((one) => (
              <PickerOption
                key={one.id}
                value={one.id}
                identifier={one.humanId}
                name={one.name}
                width={width}
              />
            ))}
          </NativeSelect>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="blocked-kind">
          {copy.blockedKind}
        </label>
        <ServerField field="kind">
          <NativeSelect
            id="blocked-kind"
            data-testid="blocked-kind"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value);
            }}
          >
            {kinds.map((one) => (
              <option key={one.token} value={one.token}>
                {one.label}
              </option>
            ))}
          </NativeSelect>
        </ServerField>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="blocked-name">
          {copy.blockedName}
        </label>
        <ServerField field="name">
          <Input
            id="blocked-name"
            data-testid="blocked-name"
            value={name}
            maxLength={200}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2" style={{ columnGap: "var(--q-s0)" }}>
        <div className="q-field">
          <label className="q-field__label" htmlFor="blocked-starts">
            {copy.blockedStarts}
          </label>
          <ServerField field="starts_on">
            <Input
              id="blocked-starts"
              data-testid="blocked-starts"
              type="date"
              value={startsOn}
              onChange={(event) => {
                setStartsOn(event.target.value);
              }}
            />
          </ServerField>
        </div>
        <div className="q-field">
          <label className="q-field__label" htmlFor="blocked-ends">
            {copy.blockedEnds}
          </label>
          <ServerField field="ends_on">
            <Input
              id="blocked-ends"
              data-testid="blocked-ends"
              type="date"
              value={endsOn}
              onChange={(event) => {
                setEndsOn(event.target.value);
              }}
            />
          </ServerField>
        </div>
      </div>
      <div className="q-field">
        <label className="q-field__label" htmlFor="blocked-note">
          {copy.blockedNote}
        </label>
        <ServerField field="note">
          <Textarea
            id="blocked-note"
            data-testid="blocked-note"
            value={note}
            maxLength={2000}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </ServerField>
      </div>
      <div className="q-actions-row">
        <button
          type="button"
          className="q-button q-mini--primary"
          data-testid="blocked-submit"
          disabled={busy || !ready}
          onClick={() => {
            void submit();
          }}
        >
          {copy.blockedSubmit}
        </button>
        <button type="button" className="q-button q-button--quiet" onClick={onClose}>
          {strings.common.cancel}
        </button>
      </div>
    </ActionDialog>
  );
}
